
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { TrainingCourseForUser } from 'app/_models/training/training-course-for-user.model';
import { TrainingCourseState } from 'app/_models/training/training-course-state';
import { NotificationService } from 'app/_services/notification.service';
import { TrainingService } from 'app/_services/training.service';
import { Globals } from './../../../_globals';
import { Pagination } from 'app/_models/pagination.model';
import { environment } from 'environments/environment';
import { ngxCsv } from 'ngx-csv';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ContractorService } from 'app/_services/contractor.service';
import { from } from 'rxjs';
import { concatMap, toArray } from 'rxjs/operators';

@Component({
  selector: 'contractor-list-for-training',
  templateUrl: './contractor-list-for-training.component.html'
})
export class ContractorListForTrainingComponent implements OnInit {

  public trainingId: number;
  public routeRole: any;
  public contractors: TrainingCourseForUser[];
  public TrainingCourseStateType = TrainingCourseState; // allow enum in template

  // pagination
  public pageSizes: any = environment.pageSizes;
  public pagination: Pagination;
  @ViewChild('paginator', { static: true }) paginator;
  public showAssignAcceptanceModal: boolean;

  public saveInProgress: boolean;
  public allContractors = []
  public assignAcceptanceForm: FormGroup;
  private siteOwnerSkylarkUserId: any

  constructor(
    private trainingService: TrainingService,
    private contractorService: ContractorService,
    private notificationService: NotificationService,
    private activatedRoute: ActivatedRoute,
    public router: Router,
    private globals: Globals,
    fb: FormBuilder,
  ) {
    this.assignAcceptanceForm = fb.group({
      contractor: new FormControl(null, [Validators.required]),
    });

    this.activatedRoute.params.subscribe((params: Params) => {
      this.trainingId = parseInt(params.trainingId);
    });
    this.routeRole = globals.user.routeRole;
    this.siteOwnerSkylarkUserId = this.globals.siteOwnerUsers[0].skylarkUserId;
  }

  ngOnInit() {
    this.resetPagination(); // call service to populate table
  }

  private fetchTableData() {
    this.trainingService.getMaintenanceListByOutage(this.trainingId, this.pagination).subscribe((response) => {
      this.contractors = response.body;
      this.pagination.totalRecords = response.totalRecords;
    }, error => {
      console.error(error);

      this.notificationService.notify('danger', 'Error loading contractors');
    });
  }

  public paginate(event) {
    this.pagination.size = event.rows;
    this.pagination.page = event.page;
    this.fetchTableData();
  }

  private resetPagination() {
    this.pagination = {
      size: this.pageSizes[0],
      page: 0,
      totalRecords: 0,
    };

    this.goToFirstPage();
    this.fetchTableData();
  }

  private goToFirstPage() {
    this.pagination.page = 0;      // Backend: reset page index to fetch from API
    if (this.paginator) {
      this.paginator.first = 0;    // Frontend: update visual page indicator (PrimeNg)
    }
  }

  public goToContractor(contractorId) {
    this.router.navigate(['/', this.routeRole, 'contractors', contractorId, 'certificates'])
  }

  public export() {
    this.trainingService.getMaintenanceListByOutage(this.trainingId, { size: 1000000, page: 0, totalRecords: 0 }).subscribe((response) => {
      this.exportData(response.body);
    }, error => {
      console.error(error);

      this.notificationService.notify('danger', 'Error fetching data for report');
    });
  }

  private exportData(csvRows: any[]) {
    const dataToExport = [];
    csvRows.forEach((element) => {
      const row = {
        id: element.contractorId,
        contractorName: element.contractorName,
        courseProgress: element.courseProgress,
        expiryDate: element.expiryDate ? new Date(element.expiryDate).toLocaleDateString('en-GB') : '',
        lastModifiedDate: new Date(element.lastModifiedDate).toLocaleDateString('en-GB'),
      };
      dataToExport.push(row);
    });

    // Export Options
    const options = {
      title: '',
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalseparator: '.',
      showLabels: true,
      showTitle: false,
      useBom: true,
      noDownload: false,
      headers: [
        'Company Id',
        'Company Name',
        'Course Progress',
        'Expiry Date',
        'Last Modifed Date',
      ],
    };

    const fileName = 'Outage-Report-' + csvRows[0].course.trainingName + new Date().toLocaleString();
    new ngxCsv(dataToExport, fileName, options);
  }

  public openAssignAcceptanceModal() {
    if (!this.allContractors.length) {
      this.getAllContractorsForDropdown();
    }
    this.assignAcceptanceForm.reset();
    this.showAssignAcceptanceModal = true;
  }

  private getAllContractorsForDropdown() {
    this.saveInProgress = true;

    this.contractorService.getLinkedContractors(this.siteOwnerSkylarkUserId, ({ size: 999, page: 0 }), 'name,asc', undefined).subscribe((data: any) => {
      this.allContractors = [
        ...data.body.map((element) => ({
          label: element.name,
          value: element.id,
        }))];
      this.saveInProgress = false;
    }, error => {
      console.error(error);
      this.saveInProgress = false;
      this.notificationService.notify('danger', 'Error retrieving contractor list');
    });
  }

  public onAssignAcceptanceFormSubmit() {
    this.saveInProgress = true;
    const values = this.assignAcceptanceForm.value;
    const selectedContractors = values.contractor;

    from(selectedContractors).pipe(
      concatMap(contractor =>
        this.trainingService.createNewAcceptanceCourseForContractor({
          courseId: this.trainingId,
          maintenanceId: (contractor as any).value,
        })
      ),
      toArray()
    ).subscribe(
      (result: any) => {
        this.notificationService.notify('success', 'Success', 'Course assigned to contractors');

        this.saveInProgress = false;
        this.showAssignAcceptanceModal = false;
        this.resetPagination();
      },
      (error) => {
        console.error(error);
        this.notificationService.notify('danger', 'Error', 'Could not add course to contractor');
        this.saveInProgress = false;
        this.showAssignAcceptanceModal = false;
        this.resetPagination();
      }
    );
  }
}
